import { useCallback, useMemo } from 'react';
// Models
import { IMessage } from 'app/models/ChatAI';
// Redux
import { useAppSelector } from 'app/hooks/useStore';
import { selectLastHumanMessage } from 'app/store/AIChat/AIChat.selectors';
// Hooks
import useCasePermissionResolved from 'app/hooks/useCasePermissionResolver';
import useAiChatPermissionResolver from 'app/hooks/useAiChatPermissionResolver';

export default function useRefineMessageUtils(message: IMessage | undefined) {
  const lastHumanMessage = useAppSelector(selectLastHumanMessage);

  const { caseAIChatEnabled, isCaseProcessingStatusNotReady } = useCasePermissionResolved();
  const { allowModifications } = useAiChatPermissionResolver();

  const getPagesInfo = useCallback((message: IMessage | undefined) => ({
    numberOfInputPages: message?.metadata?.numberOfInputPages || 0,
    numberOfAnalyzedPages: message?.metadata?.numberOfAnalyzedPages || 0
    // eslint-disable-next-line
  }), []);

  const checkIsRefineActionAllowed = useCallback((lastMessage: IMessage | undefined) => {
    // Check global and local permissions
    if (!caseAIChatEnabled || isCaseProcessingStatusNotReady || !allowModifications) return false;
    // Only allow refine for AI messages
    if (lastMessage?.type !== 'ai') return false;
    // We need to check is this last messsage from human
    // to show only for last message refine button
    if ( !lastHumanMessage || (lastHumanMessage.id !== lastMessage?.parentId) ) return false;

    const { numberOfInputPages, numberOfAnalyzedPages } = getPagesInfo(lastMessage);
    // Allow refine if there are unanalyzed pages
    return numberOfInputPages > numberOfAnalyzedPages;
  }, [lastHumanMessage, caseAIChatEnabled, isCaseProcessingStatusNotReady, allowModifications, getPagesInfo]);
  
  const analyzedPagesPercent = useMemo(() => {
    const { numberOfInputPages, numberOfAnalyzedPages } = getPagesInfo(message);
    if (numberOfInputPages === 0) return 0;
    return Math.min((numberOfAnalyzedPages / numberOfInputPages) * 100, 100);
  }, [message, getPagesInfo]);

  return {
    checkIsRefineActionAllowed,
    analyzedPagesPercent
  };
}